import { usesWebViewerServerForBlobFetch } from './filePreviewUtils';

const PROBE_BYTE_COUNT = 16384;

const OPEN_XML_EXTENSIONS = new Set(['docx', 'xlsx', 'pptx']);

const PROBE_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];

const PROTECTION_MARKERS = /EncryptedPackage|DRMContent|Microsoft Rights Label|DataSpaces|RightsManagement|Sensitivity Label/i;

export const PROTECTED_DOCUMENT_MESSAGES = {
    rights_management:
        'This file appears to be rights-managed or encrypted. Preview is not available here — open it in Microsoft 365 with your work account, or download the original if you have permission.',
    password_protected: 'This file is password-protected. Preview is not available in the dataroom viewer.',
    generic_protected:
        'This file could not be previewed because it is protected or encrypted. Try downloading the original and opening it in the native application.',
};

export const shouldProbeDocumentProtection = extension => {
    return PROBE_EXTENSIONS.includes(String(extension || '').toLowerCase());
};

const isOleCompoundFile = bytes => {
    return (
        bytes.length >= 8 &&
        bytes[0] === 0xd0 &&
        bytes[1] === 0xcf &&
        bytes[2] === 0x11 &&
        bytes[3] === 0xe0
    );
};

const isZipFile = bytes => {
    return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
};

const containsProtectionMarker = text => PROTECTION_MARKERS.test(text);

const bytesToLatin1String = bytes => {
    let result = '';
    for (let i = 0; i < bytes.length; i += 1) {
        result += String.fromCharCode(bytes[i]);
    }
    return result;
};

/**
 * Heuristic check on the first bytes of a document.
 * Returns { protected: boolean, reason?: 'rights_management' | 'password_protected' }
 */
export const detectDocumentProtectionFromBytes = (buffer, extension) => {
    const ext = String(extension || '').toLowerCase();
    if (!shouldProbeDocumentProtection(ext) || !buffer?.byteLength) {
        return { protected: false };
    }

    const bytes = new Uint8Array(buffer);
    const text = bytesToLatin1String(bytes);

    if (containsProtectionMarker(text)) {
        return { protected: true, reason: 'rights_management' };
    }

    if (ext === 'pdf') {
        if (text.includes('/Encrypt')) {
            return { protected: true, reason: 'password_protected' };
        }
        return { protected: false };
    }

    if (OPEN_XML_EXTENSIONS.has(ext)) {
        if (isOleCompoundFile(bytes) && !isZipFile(bytes)) {
            return { protected: true, reason: 'rights_management' };
        }
        return { protected: false };
    }

    if (['doc', 'xls', 'ppt'].includes(ext) && containsProtectionMarker(text)) {
        return { protected: true, reason: 'rights_management' };
    }

    return { protected: false };
};

export const getProtectedDocumentMessage = reason => {
    return PROTECTED_DOCUMENT_MESSAGES[reason] || PROTECTED_DOCUMENT_MESSAGES.generic_protected;
};

/** @returns {'excel_too_large' | 'protected' | 'generic'} */
export const classifyDocumentLoadError = error => {
    const msg = String(error?.message || error?.detail || error || '').toLowerCase();
    if (/maximum allowed cell count|total cell count has exceeded/.test(msg)) {
        return 'excel_too_large';
    }
    if (/password|encrypt|rights|drm|rms|protected|permission|information protection|irm|sensitivity/.test(msg)) {
        return 'protected';
    }
    return 'generic';
};

export async function fetchDocumentProtectionCheck(fileUrl, extension) {
    if (!fileUrl || !shouldProbeDocumentProtection(extension)) {
        return { protected: false };
    }

    // WebViewer Server fetches the blob from Azure; a browser Range probe would 403
    // when lenstorage network rules deny public client IPs.
    if (usesWebViewerServerForBlobFetch()) {
        return { protected: false };
    }

    try {
        const response = await fetch(fileUrl, {
            headers: { Range: `bytes=0-${PROBE_BYTE_COUNT - 1}` },
        });

        if (!response.ok && response.status !== 206) {
            return { protected: false };
        }

        const buffer = await response.arrayBuffer();
        return detectDocumentProtectionFromBytes(buffer, extension);
    } catch {
        return { protected: false };
    }
}
